import { createColumnHelper } from "@tanstack/react-table";
import capitalize from "lodash/capitalize";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Popup } from "semantic-ui-react";

import { ReactTable, usePermanentLocations } from "components";
import { useRegisterNavAction } from "hooks";
import { WebhooksErrorsModal, WebhooksTestModal } from "modules/WebhooksDashboard/components";
import "modules/WebhooksDashboard/components/WebhooksDashboardTable/WebhooksDashboardTable.scss";
import { useTriggersQuery, useWebhooksQuery } from "modules/WebhooksDashboard/hooks";

import type { Webhook } from "models";

type WebhooksDashboardTableProps = {
  handleShowEditWebhookModal: (webhook: Webhook) => void;
};

const columnHelper = createColumnHelper<Webhook>();

export const WebhooksDashboardTable = ({ handleShowEditWebhookModal }: WebhooksDashboardTableProps) => {
  const { t } = useTranslation();

  const [selectedTestWebhook, setSelectedTestWebhook] = useState<Webhook | null>(null);
  const [showErrorModal, setShowErrorModal] = useState<number | null>(null);

  const { data: triggers } = useTriggersQuery();
  const { locations } = usePermanentLocations();
  const { data: webhooks, isFetching: isFetchingWebhooks, refetch: refetchWebhooks } = useWebhooksQuery();

  useRegisterNavAction(refetchWebhooks);

  const handleShowErrorModal = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, webhookId: number) => {
    e.stopPropagation();
    setShowErrorModal(webhookId);
  };
  const handleShowTestModal = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, webhook: Webhook) => {
    e.stopPropagation();
    setSelectedTestWebhook(webhook);
  };

  const handleCloseErrorModal = () => setShowErrorModal(null);
  const handleCloseTestModal = () => setSelectedTestWebhook(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "name",
        size: 130,
        header: () => <span className="no-wrap-text"> {t("v8_name", "Name")} </span>,
        cell: ({
          row: {
            original: { active, name }
          }
        }) => (
          <div className="WebhooksDashboardTable__table-name-container">
            <div className={`${active ? "WebhooksDashboardTable__active-webhook" : "WebhooksDashboardTable__inactive-webhook"}`} />
            <div className="WebhooksDashboardTable__name-text">
              <span className="no-wrap-text">{name || ""}</span>
            </div>
          </div>
        )
      }),

      columnHelper.accessor(row => row, {
        id: "url",
        size: 230,
        header: () => <span className="no-wrap-text"> {t("v8_url", "URL")} </span>,
        cell: ({
          row: {
            original: { url }
          }
        }) => (
          <div className="WebhooksDashboardTable__url-container">
            <Popup hoverable content={url} trigger={<p className="WebhooksDashboardTable__url-ellipsis">{url || ""}</p>} />
          </div>
        )
      }),

      columnHelper.accessor(row => row, {
        id: "trigger",
        size: 120,
        header: () => <span className="no-wrap-text WebhooksDashboardTable__table-header-text"> {t("v8_trigger", "Trigger")} </span>,
        cell: ({
          row: {
            original: { webhook_trigger_id }
          }
        }) => {
          const trigger = triggers?.find(trigger => trigger.id === webhook_trigger_id);
          return <span className="no-wrap-text"> {t(`v8_${trigger?.name}`) || `v8_${trigger?.name}`} </span>;
        }
      }),

      columnHelper.accessor(row => row, {
        id: "headers",
        size: 40,
        header: () => <span className="WebhooksDashboardTable__table-header-text"> {t("v8_headers", "Headers")} </span>,
        cell: ({
          row: {
            original: { headers }
          }
        }) => headers?.length
      }),

      columnHelper.accessor(row => row, {
        id: "location",
        size: 100,
        header: () => <span className="no-wrap-text WebhooksDashboardTable__table-header-text"> {t("v8_location", "Location")} </span>,
        cell: ({
          row: {
            original: { dealer_location_id }
          }
        }) => {
          if (!dealer_location_id) return <span className="no-wrap-text">{t("v8_all", "All")}</span>;

          const location = locations.find(location => location.id === dealer_location_id);
          return <span className="no-wrap-text">{location?.name}</span>;
        }
      }),

      columnHelper.accessor(row => row, {
        id: "queued",
        size: 80,
        header: () => (
          <div className="WebhooksDashboardTable__table-queued-support-tickets-container">
            <span className="no-wrap-text WebhooksDashboardTable__table-header-text"> {t("v8_queued", "Queued")} </span>
          </div>
        ),
        cell: ({
          row: {
            original: { queued }
          }
        }) => <div className="WebhooksDashboardTable__table-queued-support-tickets-container">{queued ? <Icon className="circle check i-green" /> : ""}</div>
      }),

      columnHelper.accessor(row => row, {
        id: "created_by",
        size: 100,
        header: () => <span className="no-wrap-text "> {t("v8_created_by", "Created By")} </span>,
        cell: ({
          row: {
            original: { public_account, user }
          }
        }) => {
          if (public_account) return <span className="no-wrap-text">{`${t("v8_public_api", "Public API")}: ${public_account.name}`}</span>;
          if (user) return <span className="no-wrap-text">{`${t("v8_user", "User")}: ${user.first_name} ${user.last_name}`}</span>;
        }
      }),

      columnHelper.accessor(row => row, {
        id: "send_support_tickets",
        size: 50,
        header: () => (
          <div className="WebhooksDashboardTable__table-queued-support-tickets-container">
            <span className="no-wrap-text WebhooksDashboardTable__table-header-text"> {t("v8_send_support_tickets", "Send Support Tickets")} </span>
          </div>
        ),
        cell: ({
          row: {
            original: { block_creating_support_ticket }
          }
        }) => (
          <div className="WebhooksDashboardTable__table-queued-support-tickets-container">
            {block_creating_support_ticket ? <Icon className="circle remove red" /> : <Icon className="circle check i-green" />}
          </div>
        )
      }),

      columnHelper.accessor(row => row, {
        id: "actions",
        size: 30,
        header: "",
        cell: ({ row: { original } }) => (
          <div className="WebhooksDashboardTable__table-action-buttons">
            <Button icon basic className="WebhooksDashboardTable__errors-button" onClick={e => handleShowErrorModal(e, original.id)}>
              <Icon className={`rectangle list ${original.has_recent_error ? "red" : "green"}`} />
            </Button>
            <Button color="green" className="WebhooksDashboardTable__test-button" onClick={e => handleShowTestModal(e, original)}>
              {capitalize(t("v8_test", "test"))}
            </Button>
          </div>
        )
      })
    ],
    [t, triggers, locations]
  );

  return (
    <>
      <div className="WebhooksDashboardTable">
        <ReactTable isFetching={isFetchingWebhooks} columns={columns} data={webhooks} onRowClick={handleShowEditWebhookModal} />
      </div>
      <WebhooksTestModal webhook={selectedTestWebhook} onClose={handleCloseTestModal} />
      <WebhooksErrorsModal webhook_id={showErrorModal} onClose={handleCloseErrorModal} />
    </>
  );
};
